/**
 * In-memory ring buffer of the last ~20 user actions. Attached to every
 * error insert as `breadcrumbs`. Reset per session.
 *
 * Auto-collected on the client:
 *   - navigation events (route changes)
 *   - fetch calls (via trackedFetch)
 *   - explicit `addBreadcrumb()` calls from feature code
 *
 * Never contains raw user input — only shapes (URLs, statuses, click
 * target descriptions).
 */

const MAX_CRUMBS = 20

export interface Breadcrumb {
  ts: number
  kind: 'navigation' | 'fetch' | 'click' | 'custom' | 'error'
  message: string
  data?: Record<string, unknown>
}

let buffer: Breadcrumb[] = []

export function addBreadcrumb(crumb: Omit<Breadcrumb, 'ts'>): void {
  buffer.push({ ...crumb, ts: Date.now() })
  if (buffer.length > MAX_CRUMBS) buffer.shift()
}

export function getBreadcrumbs(): Breadcrumb[] {
  return [...buffer]
}

export function clearBreadcrumbs(): void {
  buffer = []
}

/**
 * Attach lightweight click tracking. Records the tagName + a short text
 * excerpt (no ids, no PII). Called from the client-side capture setup.
 */
export function attachClickBreadcrumbs(): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null
    if (!target) return
    const tag = target.tagName?.toLowerCase() || 'unknown'
    const text = (target.textContent || '').trim().slice(0, 40)
    addBreadcrumb({ kind: 'click', message: `${tag}${text ? ': ' + text : ''}` })
  }
  window.addEventListener('click', handler, { capture: true, passive: true })
  return () => window.removeEventListener('click', handler, { capture: true } as any)
}

/**
 * Attach navigation tracking. History API events don't fire naturally
 * without hooking; the simplest reliable signal is `popstate` +
 * monkey-patching pushState/replaceState. Kept scoped: only overrides
 * once, guards for repeated init.
 */
let navPatched = false
export function attachNavigationBreadcrumbs(): () => void {
  if (typeof window === 'undefined' || navPatched) return () => {}
  navPatched = true

  const emit = () => {
    addBreadcrumb({
      kind: 'navigation',
      message: window.location.pathname + window.location.search,
    })
  }

  const origPush = window.history.pushState
  const origReplace = window.history.replaceState
  window.history.pushState = function (...args) {
    origPush.apply(this, args)
    emit()
  }
  window.history.replaceState = function (...args) {
    origReplace.apply(this, args)
    emit()
  }
  window.addEventListener('popstate', emit)

  return () => {
    // We don't tear this down — the singleton lives for the tab lifetime.
    // Kept for API symmetry with the other attach* helpers.
  }
}
